import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';
import { Card } from '@/components/ui/card';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

export default function Analytics() {
    const { brandId, brands } = useScope();
    const [data, setData] = useState(null);

    useEffect(() => {
        const params = {};
        if (brandId) params.brand_id = brandId;
        apiClient.get('/dashboard/analytics', { params }).then((r) => setData(r.data));
    }, [brandId]);

    if (!data) return <div className="text-sm text-muted-foreground">Loading analytics…</div>;

    const brandRows = Object.entries(data.by_brand || {}).map(([id, v]) => ({
        id,
        name: v.name,
        color: `hsl(${brandColorVar(v.color_slot)})`,
        drafts: v.drafts,
        scheduled: v.scheduled,
        published: v.published,
        comments: v.comments,
    }));

    const classData = (data.comment_classifications || []).map(([k, v]) => ({ category: k, count: v }));
    const typeData = (data.top_content_types || []).map(([k, v]) => ({ type: k, count: v }));

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
                <p className="text-sm text-muted-foreground">Operator-level insight across brands, accounts, and content types.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="border-border bg-card p-4">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Totals</div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                        <div><div className="text-2xl font-semibold">{data.totals.drafts}</div><div className="text-[11px] text-muted-foreground">drafts</div></div>
                        <div><div className="text-2xl font-semibold">{data.totals.published}</div><div className="text-[11px] text-muted-foreground">publishes</div></div>
                        <div><div className="text-2xl font-semibold">{data.totals.comments}</div><div className="text-[11px] text-muted-foreground">comments</div></div>
                    </div>
                </Card>
                <Card className="border-border bg-card p-4">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Top content types</div>
                    <div className="mt-2 h-40">
                        {typeData.length === 0 ? (
                            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No data yet.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={typeData}>
                                    <CartesianGrid stroke="hsl(var(--gridline))" strokeDasharray="3 3" />
                                    <XAxis dataKey="type" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                                    <Tooltip cursor={{ fill: 'hsl(var(--surface-3))' }} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 11 }} />
                                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </Card>
                <Card className="border-border bg-card p-4">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Comment categories</div>
                    <div className="mt-2 h-40">
                        {classData.length === 0 ? (
                            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No classified comments yet.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={classData} dataKey="count" nameKey="category" innerRadius={28} outerRadius={50} stroke="hsl(var(--background))">
                                        {classData.map((entry, idx) => (
                                            <Cell key={idx} fill={`hsl(var(--brand-accent-${(idx % 5) + 1}))`} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </Card>
            </div>
            <Card className="border-border bg-card p-4">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">By brand</div>
                {brandRows.length === 0 ? (
                    <div className="py-6 text-sm text-muted-foreground">Scope is a single brand or no brands yet.</div>
                ) : (
                    <ul className="mt-2 divide-y divide-border">
                        {brandRows.map((b) => (
                            <li key={b.id} className="grid grid-cols-[20px_1fr_80px_80px_80px_80px] items-center gap-2 px-1 py-2 text-sm">
                                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                                <span>{b.name}</span>
                                <span className="text-right font-mono text-xs">{b.drafts} drafts</span>
                                <span className="text-right font-mono text-xs">{b.scheduled} sched</span>
                                <span className="text-right font-mono text-xs">{b.published} pub</span>
                                <span className="text-right font-mono text-xs">{b.comments} cmts</span>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
}
